import React, { useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { Button, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';

import { useGetBrowsableLensSpaces } from '~/hooks/apis/reporting/useLensSpaces';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { LensResponse } from '~/types/leLens';

import ShareLensDialog from '../ShareLensDialog';
import { exactViewHref } from '../personalViews/viewState';

import type { LensState } from './types';

type LensShareProps = {
  detail: LensResponse;
  state: LensState;
  narrow: boolean;
};

export default function LensShare({ detail, state, narrow }: LensShareProps) {
  const { lens, access } = detail;
  const anchor = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState(false);
  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const spaces = useGetBrowsableLensSpaces(dialog);
  const copy = async () => {
    setMenuOpen(false);
    let link: string;
    try {
      link = exactViewHref('document', window.location, lens.version);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'The link was not copied. Try again.', { variant: 'warning' });
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      enqueueSnackbar('Link to this view copied', { variant: 'success' });
    } catch {
      enqueueSnackbar('The link was not copied. Try again.', { variant: 'warning' });
    }
  };
  const open = () => {
    setMenuOpen(false);
    setDialog(true);
  };

  return (
    <>
      {narrow ? (
        <Tooltip title="Share">
          <IconButton aria-haspopup="menu" aria-label="Share" onClick={() => setMenuOpen(true)} ref={anchor} size="small">
            <ShareOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button aria-haspopup="menu" onClick={() => setMenuOpen(true)} ref={anchor} size="small" startIcon={<ShareOutlinedIcon fontSize="small" />} sx={{ whiteSpace: 'nowrap' }} variant="outlined">
          Share
        </Button>
      )}
      <Menu anchorEl={anchor.current} onClose={() => setMenuOpen(false)} open={menuOpen}>
        <MenuItem autoFocus onClick={() => void copy()}>
          <ListItemIcon>
            <LinkOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Copy link to this view
        </MenuItem>
        <MenuItem onClick={open}>
          <ListItemIcon>
            <GroupAddOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Share with people…
        </MenuItem>
        <MenuItem onClick={open}>
          <ListItemIcon>
            <DriveFileMoveOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Move to a team…
        </MenuItem>
      </Menu>
      {dialog ? (
        <ShareLensDialog
          documentState={state}
          lens={{
            ...lens,
            access: {
              role: null,
              canRead: true,
              canPropose: false,
              canReview: false,
              canRestore: false,
              canShare: false,
              canRequestCertification: false,
              canCertify: false,
              canRemoveCertification: false,
              ...access,
            },
            home: detail.sharing?.home ?? lens.home,
            audience: detail.sharing?.audience ?? lens.audience,
          }}
          onClose={() => setDialog(false)}
          open
          spaces={spaces.data?.spaces ?? []}
          spacesError={spaces.isError}
          spacesLoading={spaces.isLoading}
          viewerEmail={user?.email}
        />
      ) : null}
    </>
  );
}
